import { TimeoutError } from "@/backend/TimeoutError";

/**
 * Wraps a function that can throw a TimeoutError.
 * When a TimeoutError is thrown, it checks if (in the meantime) there was another call to this function with the same ID.
 * If so, the TimeoutError callback is not executed (but the Error is still thrown).
 */
export function timeoutHandling<A extends unknown[], R>(
  makeId: (...args: A) => string,
  action: (...args: A) => Promise<R>,
  onTimeout: (...args: A) => void,
): (...args: A) => Promise<R> {
  const starts: Record<string, number> = {};
  return async (...args: A) => {
    const start = Date.now();
    const id = makeId(...args);
    starts[id] = start;
    try {
      return await action(...args);
    } catch (e) {
      if (e instanceof TimeoutError && starts[id] === start) {
        onTimeout(...args);
      }
      throw e;
    } finally {
      delete starts[id];
    }
  };
}
